import { seatKinds, type SeatKind } from '@trippath/shared';
import { useTranslation } from 'react-i18next';

/** Where a seat is, drawn: an aircraft's window, three seats the middle one lit, one by the aisle. */
export function SeatKindIcon({ kind }: { kind: SeatKind }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {kind === 'window' && (
        <>
          <rect x="6" y="2" width="12" height="20" rx="6" />
          <rect x="9" y="6" width="6" height="12" rx="3" />
        </>
      )}
      {kind === 'middle' && (
        <>
          <rect x="1.5" y="5" width="5.5" height="14" rx="2" />
          <rect x="9.25" y="5" width="5.5" height="14" rx="2" fill="currentColor" />
          <rect x="17" y="5" width="5.5" height="14" rx="2" />
        </>
      )}
      {kind === 'aisle' && (
        <>
          <rect x="2" y="5" width="8" height="14" rx="2" />
          <path d="M16 4v2M16 10v2M16 16v2" />
          <path d="M20 4v16" />
        </>
      )}
    </svg>
  );
}

interface Props {
  /** The seat it says, in the buttons' names: `24E`. */
  seat: string;
  /** As picked; none when not. */
  value: SeatKind | null;
  /** As the aircraft's seat map says it: then it is that, not to be picked. */
  known: SeatKind | null;
  onChange: (kind: SeatKind | null) => void;
}

/**
 * Where a seat is — by a window, in the middle, on an aisle — by three marks, one pressed; pressed
 * again it is not said. The aircraft's seat map, when known, says it instead.
 */
export function SeatKindPick({ seat, value, known, onChange }: Props) {
  const { t } = useTranslation();
  const shown = known ?? value;
  return (
    <div
      className="segmented segmented-small seat-kinds"
      role="group"
      aria-label={t('seatMap.kindOf', { seat })}
    >
      {seatKinds.map((kind) => {
        const said =
          known === null
            ? t(`seatMap.kind.${kind}`)
            : t('seatMap.kindByMap', { kind: t(`seatMap.kind.${known}`) });
        return (
          <button
            key={kind}
            type="button"
            aria-pressed={shown === kind}
            aria-label={t(`seatMap.kind.${kind}`)}
            title={said}
            disabled={known !== null}
            onClick={() => {
              onChange(value === kind ? null : kind);
            }}
          >
            <SeatKindIcon kind={kind} />
          </button>
        );
      })}
    </div>
  );
}

/** A seat map: the aircraft from above, nose up, its seats in rows. */
export function SeatMapIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2c2 0 3 2 3 4v14l-3 2-3-2V6c0-2 1-4 3-4ZM9 11l-7 3M15 11l7 3" />
      <path d="M11 8h0M13 8h0M11 11h0M13 11h0M11 14h0M13 14h0" strokeWidth="2.6" />
    </svg>
  );
}
